import { Avatar, Button, DatePicker, Dropdown, Layout, message, Modal, Tag, Tooltip, Typography, Grid } from "antd";
import type { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import EmailSideBar from "components/EmailDashboard/email-sidebar";
import EmailDetail from "components/EmailDashboard/email-detail";
import { ApiService } from "api/api.service";

import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import DashboardLayout from "layout/DashboardLayout";
import PageTitle from "components/PageTitle";
import {
  TbDotsVertical,
  TbInfoCircle,
  TbListCheck,
  TbPlus,
  TbRefresh,
  TbSend,
  TbTrash,
  TbUser,
  TbUsersPlus,
  TbX,
} from "react-icons/tb";
import ComposeEmail from "components/EmailDashboard/ComposeEmail";

import { timeAgo } from "utils/functions";
import dayjs from "dayjs";

import { useQueryClient } from "@tanstack/react-query";
import { LuUser } from "react-icons/lu";
import { RoleTypes } from "dtos/user.dto";
import { ManageAclModal } from "./ManageAcl";
import SmartNegotiationPanel from "components/EmailDashboard/SmartNegotiation";
import { colors } from "components/EmailDashboard/email-message";
import OfferAssistant from "components/EmailDashboard/OfferAssistant/OfferAssistant";

const { Content } = Layout;

export default function EmailDashboard() {
  const { app_key, orgCode } = useParams<{ app_key: string; orgCode: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [sp, setSp] = useSearchParams();
  const fromApp = decodeURIComponent(sp.get("fromApp") || "");
  const selectedLoan = decodeURIComponent(sp.get("selectedLoan") || "");
  const threadId = sp.get("threadId");
  const getAllOrgRes = ApiService.organisation.useGetPaginated({ page: 1, limit: 20 });
  const getAllOrgData = getAllOrgRes.data?.result.data ?? [];
  const [selectedEmailId, setSelectedEmailId] = useState<string | undefined>(threadId || undefined);
  const messageId = sp.get("messageId");
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | undefined>(messageId || undefined);
  const [isInboxPanelOpen, setIsInboxPanelOpen] = useState(true);
  const [selectedProfileId, setSelectedProfileId] = useState<string | undefined>(undefined);
  const [selectedEmails, setSelectedEmails] = useState<string | undefined>(undefined);
  const [selectedProfileIdToSync, setSelectedProfileIdToSync] = useState<string | undefined>(undefined);
  const [openSyncModal, setOpenSyncModal] = useState<boolean>(false);
  const [openAclModal, setOpenAclModal] = useState<boolean>(false);
  const [isNegotiationPanelOpen, setIsNegotiationPanelOpen] = useState(false);
  const [composeModalOpen, setComposeModalOpen] = useState(() => sp.get("compose") === "true");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [smartNegotiationContext, setSmartNegotiationContext] = useState<{
    application_id?: string;
    loan_id?: string;
    thread_id?: string;
    message_id?: string;
  }>({ application_id: undefined, loan_id: undefined, message_id: undefined, thread_id: undefined });
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const attachedProfiles = ApiService.email.useGetAttachedProfiles({ appKey: app_key });
  const getOrganisationsProfile = ApiService.email.useGetOrganisationsProfiles({ orgCode: orgCode });
  const getUserQuery = ApiService.user.useGetCurrentUser();

  const switchProfile = ApiService.email.useSwitchProfile();
  const switchOrgProfile = ApiService.email.useSwitchOrgProfile();
  // const syncEmails = ApiService.email.useSyncEmails();
  const switchDefaultProfile = ApiService.email.useSwitchDefaultSenderProfile();
  // const user = ApiService.user.useGetCurrentUser();
  const fullApplication = ApiService.application.useGetByAppKey(app_key);
  // const chatId = decodeURIComponent(sp.get("ncId") || "");
  // const parentChatId = decodeURIComponent(sp.get("pncId") || "");
  // const chatQuery = ApiService.communication.useGetChatById(chatId);
  // const parentChatQuery = ApiService.communication.useGetChatById(parentChatId);
  // const chat = chatQuery.data?.result;
  // const parentChat = parentChatQuery.data?.result;
  // const isSuperAdmin = useAppSelector((state) => state.user.role === RoleTypes.SUPERADMIN);

  const allProfiles = (app_key ? attachedProfiles : getOrganisationsProfile).data?.result ?? [];
  const hasNoProfiles = allProfiles.length === 0;

  const openComposeModal = () => {
    setComposeModalOpen(true);
    setSp((prev) => {
      prev.set("compose", "true");
      return prev;
    });
  };

  const closeComposeModal = () => {
    setComposeModalOpen(false);
    setSp((prev) => {
      prev.set("compose", "false");
      return prev;
    });
  };

  useEffect(() => {
    setSp((prev) => {
      prev.delete("messageId");
      if (selectedEmailId) {
        prev.set("threadId", selectedEmailId);
      } else {
        prev.delete("threadId");
      }
      return prev;
    });
  }, [selectedEmailId]);

  // useEffect(() => {
  //   if (parentChat) {
  //     setSelectedEmailId(parentChat.mail_thread_id);
  //     setSp((prev) => {
  //       if (parentChat.mail_thread_id) prev.set("threadId", parentChat.mail_thread_id);
  //       if (parentChat.mail_message_id) prev.set("replyTo", parentChat.mail_message_id);
  //       return prev;
  //     });
  //   } else if (!parentChatQuery.isLoading && chat?.channel === "email") {
  //     openComposeModal();
  //     setSp((prev) => {
  //       const existing = Object.fromEntries(prev.entries());
  //       return {
  //         ...existing,
  //         subject: chat.subject,
  //         body: chat.body,
  //       };
  //     });
  //   }
  // }, [chatQuery, parentChatQuery]);

  const selectedOrgProfiles = getAllOrgData.find((profile) => profile.slug === orgCode);

  useEffect(() => {
    // const selectedProfile = !orgCode
    //   ? (attachedProfiles.data?.result.find((profile) => profile.selected) ?? attachedProfiles.data?.result[0])
    //   : (getOrganisationsProfile.data?.result.find((profile) => profile.selected) ??
    //     getOrganisationsProfile.data?.result[0]);
    const selectedProfile = allProfiles.find((pf) => pf.selected) ?? allProfiles.at(0);

    if (selectedProfile?._id) {
      setSelectedProfileId(selectedProfile._id);
      setSelectedEmails(selectedProfile.email);
    }
  }, [allProfiles]);

  const screen = Grid.useBreakpoint();

  const changeSelectedThread = (thread: string | undefined) => {
    setSelectedEmailId(thread);
    setIsNegotiationPanelOpen(false);
    setSp((prev) => {
      if (thread) prev.set("threadId", thread);
      return prev;
    });
  };

  return (
    <DashboardLayout
      headerRight={
        <div className="flex items-center gap-2  h-[56px] w-full">
          {app_key && (
            <Button
              size="middle"
              icon={<TbUsersPlus />}
              color="magenta"
              variant="solid"
              onClick={() => {
                setAddModalOpen(true);
              }}
            >
              {screen.sm && "Add Additional Emails"}
            </Button>
          )}
          {composeModalOpen ? (
            <Button
              type="primary"
              size="middle"
              className=""
              icon={<TbX />}
              onClick={() => {
                closeComposeModal();
                changeSelectedThread(undefined);
              }}
            >
              {screen.sm && "Close Compose Email"}
            </Button>
          ) : (
            <Button
              type="primary"
              size="middle"
              className=""
              icon={<TbSend />}
              onClick={() => {
                openComposeModal();
                changeSelectedThread(undefined);
              }}
            >
              {screen.sm && "Compose New Email"}
            </Button>
          )}
          {hasNoProfiles ? (
            <Button
              icon={<TbPlus />}
              type="default"
              size="middle"
              onClick={() => setConnectModalOpen(true)}
              className=""
            >
              Add Account
            </Button>
          ) : (
            <Dropdown
              trigger={["click"]}
              menu={{
                selectedKeys: [selectedProfileId || ""],
                items: [
                  ...((app_key ? attachedProfiles.data?.result : getOrganisationsProfile.data?.result) || []).map(
                    (profile) => ({
                      key: profile._id,
                      className: selectedProfileId === profile._id ? "bg-indigo-50" : "opacity-70",
                      style: {
                        borderWidth: 2,
                        borderStyle: "solid",
                        borderColor: selectedProfileId === profile._id ? "#e0e7ff" : "transparent",
                      },
                      label: (
                        <div className="flex items-center gap-2 w-full">
                          <div className={`flex items-center gap-3`}>
                            <div
                              role="button"
                              onClick={() => {
                                setSelectedProfileId(profile._id);
                                setSelectedEmails(profile.email);
                                if (app_key) {
                                  switchProfile.mutate(
                                    { profileId: profile._id, appKey: app_key },
                                    {
                                      onSuccess: () => {
                                        message.success("Profile switched successfully");
                                        changeSelectedThread(undefined);
                                      },
                                    },
                                  );
                                } else if (orgCode) {
                                  switchOrgProfile.mutate(
                                    { profileId: profile._id, orgCode: orgCode },
                                    {
                                      onSuccess: () => {
                                        message.success("Profile switched successfully");
                                        changeSelectedThread(undefined);
                                      },
                                    },
                                  );
                                }
                              }}
                              className="flex items-center gap-3 w-52"
                            >
                              <Avatar
                                style={{
                                  backgroundColor:
                                    colors[(profile.email.toUpperCase().charCodeAt(0) - 65) % colors.length]["text"],
                                  color: colors[(profile.email.toUpperCase().charCodeAt(0) - 65) % colors.length]["bg"],
                                  aspectRatio: 1,
                                }}
                                className={`uppercase font-semibold min-w-8`}
                                src={profile.avatarUrl}
                              >
                                {profile.email.charAt(0).toUpperCase()}
                              </Avatar>
                              <div className="flex flex-col items-start">
                                <Typography.Text type="secondary" className="text-xs line-clamp-1">
                                  {profile.email}
                                </Typography.Text>
                                <Typography.Text type="secondary" className="text-xs">
                                  {profile.lastSyncAt
                                    ? `Last synced ${timeAgo(profile.lastSyncAt)} ago`
                                    : "Never synced"}
                                </Typography.Text>
                                {profile.isDefaultSender && (
                                  <div className="px-1 rounded bg-indigo-500">
                                    <Typography.Text type="secondary" className="text-[10px] !text-white">
                                      Default Sender
                                    </Typography.Text>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="ml-auto flex items-center gap-2">
                              <Dropdown
                                trigger={["click"]}
                                menu={{
                                  items: [
                                    orgCode
                                      ? {
                                          key: "default-profile",
                                          icon: <LuUser />,
                                          label: "Set as Default Profile",
                                          onClick: (e) => {
                                            if (orgCode) {
                                              switchDefaultProfile.mutate(
                                                { profileId: profile._id, orgCode },
                                                {
                                                  onSuccess: () => {
                                                    message.success("Default profile switched successfully");
                                                  },
                                                },
                                              );
                                            }
                                          },
                                        }
                                      : null,
                                    {
                                      key: "sync-profile",
                                      icon: <TbRefresh />,
                                      label: "Sync Profile",
                                      onClick: (e) => {
                                        setSelectedProfileIdToSync(profile._id);
                                        setOpenSyncModal(true);
                                      },
                                    },
                                    profile.acl
                                      ? {
                                          key: "manage-acl",
                                          icon: <TbListCheck />,
                                          label: "Manage ACL",
                                          onClick: () => {
                                            setSelectedProfileIdToSync(profile._id);
                                            setOpenAclModal(true);
                                          },
                                        }
                                      : null,
                                  ],
                                }}
                              >
                                <Button
                                  icon={<TbDotsVertical />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                  }}
                                ></Button>
                              </Dropdown>

                              <DisconnectProfileModal
                                threadsCount={profile.threadsCount}
                                messageCount={profile.messagesCount}
                                profileId={profile._id}
                                appKey={app_key}
                                orgCode={orgCode}
                              >
                                <Tooltip title="Disconnect Profile">
                                  <Button danger icon={<TbTrash />} />
                                </Tooltip>
                              </DisconnectProfileModal>
                            </div>
                          </div>
                        </div>
                      ),
                    }),
                  ),
                  {
                    key: "add-account",
                    label: (
                      <Button icon={<TbPlus />} block type="dashed" onClick={() => setConnectModalOpen(true)}>
                        Add More Profiles
                      </Button>
                    ),
                  },
                ],
              }}
            >
              <Button icon={<TbUser />} type="default" size="middle" className="">
                {selectedEmails ? (
                  <span className="max-w-[100px] truncate">{selectedEmails}</span>
                ) : (
                  "Select Email Profile"
                )}
                {!hasNoProfiles && (
                  <Tag className="m-0! text-inherit!">
                    {app_key ? attachedProfiles.data?.result?.length : getOrganisationsProfile.data?.result?.length}
                  </Tag>
                )}
              </Button>
            </Dropdown>
          )}
          {orgCode && getUserQuery?.data?.role === RoleTypes.SUPERADMIN && (
            <Dropdown
              trigger={["click"]}
              menu={{
                selectedKeys: [orgCode || ""],
                items: [
                  ...getAllOrgData.map((profile) => ({
                    key: profile.slug,
                    className: orgCode === profile.slug ? "bg-indigo-50" : "",
                    style: {
                      borderWidth: 2,
                      borderStyle: "solid",
                      borderColor: orgCode === profile.slug ? "#e0e7ff" : "transparent",
                    },
                    label: (
                      <div className="flex items-center gap-2 w-full">
                        <div className={`flex items-center gap-3`}>
                          <div
                            role="button"
                            onClick={() => {
                              // setSelectedProfileId(profile._id);
                              // if (app_key) {
                              //   switchProfile.mutate(
                              //     { profileId: profile._id, appKey: app_key },
                              //     {
                              //       onSuccess: () => {
                              //         message.success("Profile switched successfully");
                              //         setSelectedEmailId(undefined);
                              //       },
                              //     },
                              //   );
                              // } else if (orgCode) {
                              //   switchOrgProfile.mutate(
                              //     { profileId: profile._id, orgCode: orgCode },
                              //     {
                              //       onSuccess: () => {
                              //         message.success("Profile switched successfully");
                              //         setSelectedEmailId(undefined);
                              //       },
                              //     },
                              //   );
                              // }

                              navigate(`/dashboard/emails/${profile.slug}`, { replace: true });
                              queryClient.invalidateQueries({ queryKey: ["org-emails"] });
                              setSelectedEmailId(undefined);
                              setSelectedProfileId(undefined);
                              setSelectedEmails(undefined);
                            }}
                            className="flex items-center gap-3 w-36"
                          >
                            <Avatar
                              // style={{
                              //   backgroundColor:
                              //     colors[(profile.email.toUpperCase().charCodeAt(0) - 65) % colors.length]["text"],
                              //   color: colors[(profile.email.toUpperCase().charCodeAt(0) - 65) % colors.length]["bg"],
                              //   aspectRatio: 1,
                              // }}
                              className={`uppercase font-semibold`}
                              src={profile.meta?.favicon}
                            >
                              {profile.name.charAt(0).toUpperCase()}
                            </Avatar>
                            <div className="flex flex-col items-start">
                              <Typography.Text type="secondary" className="text-xs">
                                {profile.name}
                              </Typography.Text>
                            </div>
                          </div>
                        </div>
                      </div>
                    ),
                  })),
                ],
              }}
            >
              <Button type="default" size="middle" className="">
                <div className="flex items-center gap-3 ">
                  <Avatar size={"small"} className={`uppercase font-semibold`} src={selectedOrgProfiles?.meta?.favicon}>
                    {selectedOrgProfiles?.name?.charAt(0).toUpperCase()}
                  </Avatar>
                  <div className="flex flex-col items-start">
                    <Typography.Text type="secondary" className="text-xs">
                      {selectedOrgProfiles?.name}
                    </Typography.Text>
                  </div>
                </div>
              </Button>
            </Dropdown>
          )}
        </div>
      }
    >
      <PageTitle
        pageTitle={[
          app_key ? { title: "Applications", link: `/dashboard/applications` } : { title: "Communications" },
          ...(app_key
            ? [
                {
                  title:
                    fullApplication.data?.data?.application?.client_details?.name ??
                    fullApplication.data?.application?.meta?.client_name ??
                    "Application Item",
                  link: `/dashboard/application/${app_key}`,
                },
              ]
            : [
                {
                  title:
                    fullApplication.data?.data?.application?.client_details?.name ??
                    fullApplication.data?.application?.meta?.client_name ??
                    "Email Inbox",
                },
              ]),
          ...(app_key ? [{ title: "Emails" }] : []),
        ]}
        title={app_key ? "Emails" : "Organisations Emails"}
      />
      <Layout className=" h-[calc(100vh-50px)] overflow-hidden ">
        <EmailSideBar
          selectedEmailId={selectedEmailId}
          setSelectedEmailId={changeSelectedThread}
          closeComposeModal={closeComposeModal}
          connectModalOpen={connectModalOpen}
          setConnectModalOpen={setConnectModalOpen}
          refetchProfile={() => attachedProfiles.refetch()}
          addModalOpen={addModalOpen}
          setAddModalOpen={setAddModalOpen}
          selectedProfileId={selectedProfileId}
          isInboxPanelOpen={isInboxPanelOpen}
          closeInboxPanelOpen={() => setIsInboxPanelOpen(false)}
        />

        <Layout className=" overflow-auto ">
          {composeModalOpen ? (
            <Content className="bg-white h-full flex overflow-hidden">
              <div className="flex-1 min-w-0 overflow-y-auto">
                <ComposeEmail
                  attachedProfile={allProfiles}
                  selectedProfileId={selectedProfileId}
                  isPanelOpen={isNegotiationPanelOpen}
                  toggleNegotiationPanel={() => setIsNegotiationPanelOpen((prev) => !prev)}
                  isInboxPanelOpen={isInboxPanelOpen}
                  openInboxPanel={() => setIsInboxPanelOpen(true)}
                  closeInboxPanel={() => setIsInboxPanelOpen(false)}
                  updateNegotiationContext={(context) => setSmartNegotiationContext(context)}
                />
              </div>

              {/* <SmartNegotiationPanel
                isOpen={isNegotiationPanelOpen}
                onClose={() => setIsNegotiationPanelOpen(false)}
                negotiationContext={smartNegotiationContext}
                isShowInDrawer={false}
              /> */}
              <OfferAssistant
                isOpen={isNegotiationPanelOpen}
                onClose={() => setIsNegotiationPanelOpen(false)}
                negotiationContext={smartNegotiationContext}
                clientName=""
              />
            </Content>
          ) : (
            <Content className="bg-white h-full overflow-hidden">
              <div className="flex h-full overflow-hidden">
                <EmailDetail
                  emailThreadId={selectedEmailId}
                  messageId={highlightedMessageId}
                  profile={allProfiles.find((pf) => pf._id.toString() === selectedProfileId?.toString())}
                  isPanelOpen={isNegotiationPanelOpen}
                  toggleNegotiationPanel={() => setIsNegotiationPanelOpen((prev) => !prev)}
                  isInboxPanelOpen={isInboxPanelOpen}
                  openInboxPanel={() => setIsInboxPanelOpen(true)}
                  closeInboxPanel={() => setIsInboxPanelOpen(false)}
                  updateNegotiationContext={(context) => setSmartNegotiationContext(context)}
                />
                {/* <SmartNegotiationPanel
                  isOpen={isNegotiationPanelOpen}
                  onClose={() => setIsNegotiationPanelOpen(false)}
                  negotiationContext={smartNegotiationContext}
                  isShowInDrawer={false}
                /> */}
                <OfferAssistant
                  isOpen={isNegotiationPanelOpen}
                  onClose={() => setIsNegotiationPanelOpen(false)}
                  negotiationContext={smartNegotiationContext}
                  clientName=""
                />
              </div>
            </Content>
          )}
        </Layout>
      </Layout>
      <SyncModal
        open={openSyncModal}
        onClose={() => setOpenSyncModal(false)}
        selectedProfileId={selectedProfileIdToSync}
        app_key={app_key}
      />
      <ManageAclModal
        appKey={app_key}
        profile={selectedProfileIdToSync}
        open={openAclModal}
        onCancel={() => setOpenAclModal(false)}
        okText="Save"
        title="Manage Access Control List"
      />
    </DashboardLayout>
  );
}

interface SyncModalProps {
  open: boolean;
  onClose: () => void;
  selectedProfileId?: string;
  app_key?: string;
}

const SyncModal = ({ onClose, open, selectedProfileId, app_key }: SyncModalProps) => {
  const syncEmails = ApiService.email.useSyncEmails();
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs());
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());

  const handleSyncProfile = () => {
    if (app_key) {
      syncEmails.mutate(
        {
          appKey: app_key,
          profileId: selectedProfileId as string,
          startDate,
          endDate,
        },
        {
          onSuccess: () => {
            message.success("Sync initiated successfully");
            setStartDate(null);
            setEndDate(null);
            onClose();
          },
        },
      );
    } else {
      syncEmails.mutate(
        {
          profileId: selectedProfileId as string,
          startDate,
          endDate,
        },
        {
          onSuccess: () => {
            message.success("Sync initiated successfully");
            setStartDate(null);
            setEndDate(null);
            onClose();
          },
        },
      );
    }
  };
  const today = dayjs().endOf("day");

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={handleSyncProfile}
      title="Sync Emails"
      okButtonProps={{ loading: syncEmails.isPending }}
      okText="Sync"
    >
      <div>
        <div className="flex items-center gap-4">
          <div className="flex flex-col items-start">
            <Typography.Paragraph>Start Date</Typography.Paragraph>
            <DatePicker
              value={startDate}
              onChange={(date: Dayjs | null) => setStartDate(date)}
              disabledDate={(current) => !!(current && (current > today || (endDate && current > endDate)))}
            />
          </div>

          <div className="flex flex-col items-start">
            <Typography.Paragraph>End Date</Typography.Paragraph>
            <DatePicker
              value={endDate}
              onChange={(date: Dayjs | null) => setEndDate(date)}
              disabledDate={(current) => !!(current && (current > today || (startDate && current < startDate)))}
            />
          </div>
        </div>
        <div className="flex items-center gap-2 mt-2">
          <TbInfoCircle className="stroke-gray-400" size={20} />
          <Typography.Text type="secondary" className="!text-xs">
            You may select a date range to sync emails. This step is optional, if no date range is selected, all emails
            will be synced by default.
          </Typography.Text>{" "}
        </div>
      </div>
    </Modal>
  );
};

const DisconnectProfileModal = ({
  threadsCount,
  messageCount,
  children: trigger,
  profileId,
  appKey,
  orgCode,
}: {
  threadsCount: number;
  messageCount: number;
  children: React.ReactNode;
  profileId: string;
  appKey: string | undefined;
  orgCode: string | undefined;
}) => {
  const [open, setOpen] = useState(false);
  const disconnectProfile = ApiService.email.useDisconnectProfile();
  const disconnectOrgProfile = ApiService.email.useDisconnectOrgProfile();
  const queryClient = useQueryClient();

  return (
    <div>
      <div onClick={() => setOpen(true)}>{trigger}</div>
      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        title="Disconnect Profile"
        okButtonProps={{ danger: true, loading: disconnectProfile.isPending }}
        okText="Disconnect"
        onOk={() => {
          if (appKey) {
            disconnectProfile.mutate(
              {
                profileId: profileId,
                appKey: appKey,
              },
              {
                onSuccess: () => {
                  message.success("Disconnected profile from this application");
                  queryClient.invalidateQueries({ queryKey: ["attached-profiles"] });
                  setOpen(false);
                },
              },
            );
          } else if (orgCode) {
            disconnectOrgProfile.mutate(
              { orgCode, profileId },
              {
                onSuccess: () => {
                  message.success("Disconnected profile from this application");
                  queryClient.invalidateQueries({ queryKey: ["org-attached-profiles"] });
                  setOpen(false);
                },
              },
            );
          }
        }}
      >
        <Typography.Text strong>Are you sure you want to disconnect this profile?</Typography.Text>
        <br />
        <Typography.Paragraph>
          You will no longer be able to send or receive emails from this profile within this application. All associated
          data will also be removed.
        </Typography.Paragraph>
        <Typography.Paragraph>
          This profile has <Typography.Text strong>{threadsCount}</Typography.Text> email threads and total of{" "}
          <Typography.Text strong>{messageCount}</Typography.Text> messages associated with it.
        </Typography.Paragraph>
      </Modal>
    </div>
  );
};
